import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { SlidersHorizontal, Plus, Trash2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Pannello admin: rettifiche manuali agli appuntamenti (aggiunte "virtuali").
 * Somma un numero di appuntamenti reali NON presenti in GHL a un giorno
 * specifico; il valore compare nei riepiloghi mensili (KPI + grafici) come
 * "solventi", è tracciato (autore/nota/data) e rimovibile in qualsiasi momento.
 */
export default function ManualAdjustmentsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [count, setCount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dashboard/manual-adjustments`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Impossibile caricare le rettifiche");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!date) { toast.error("Seleziona una data"); return; }
    const n = parseInt(count, 10);
    if (Number.isNaN(n) || n < 0) { toast.error("Inserisci un numero valido"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/dashboard/manual-adjustments`,
        { date, count: n, note }, { withCredentials: true });
      toast.success(`Rettifica salvata: +${n} il ${date}`);
      setDate(""); setCount(""); setNote("");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    try {
      await axios.delete(`${API}/dashboard/manual-adjustments/${d}`, { withCredentials: true });
      toast.success(`Rettifica del ${d} rimossa`);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error("Rimozione non riuscita");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="manual-adjustments-panel">
      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
        <SlidersHorizontal className="w-4 h-4 text-amber-500" /> Rettifiche manuali appuntamenti
      </h3>
      <p className="text-[11px] text-slate-500 mt-1 mb-3 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Aggiunge appuntamenti reali NON presenti in GHL a un giorno specifico.
        Compaiono nei riepiloghi mensili come "solventi", con nota e autore. Reversibile.
      </p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Data</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="h-9 rounded-xl text-sm" data-testid="adj-date-input" />
        </div>
        <div className="w-24">
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Quantità</label>
          <Input type="number" min="0" value={count} onChange={(e) => setCount(e.target.value)}
            placeholder="es. 82" className="h-9 rounded-xl text-sm" data-testid="adj-count-input" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Nota (motivo)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="es. persi in GHL" className="h-9 rounded-xl text-sm" data-testid="adj-note-input" />
        </div>
        <Button onClick={save} disabled={saving}
          className="h-9 rounded-xl bg-amber-500 hover:bg-amber-600 text-white" data-testid="adj-save-btn">
          <Plus className="w-4 h-4 mr-1" /> {saving ? "Salvo…" : "Aggiungi"}
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="adj-empty">Nessuna rettifica manuale attiva.</p>
      ) : (
        <div className="space-y-1.5" data-testid="adj-list">
          {items.map((it) => (
            <div key={it.date} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
              <div className="min-w-0">
                <span className="font-semibold text-slate-800">{it.date}</span>
                <span className="ml-2 text-amber-600 font-bold">+{it.count}</span>
                {it.note && <span className="ml-2 text-slate-500 truncate">— {it.note}</span>}
                {it.created_by && <span className="ml-2 text-[10px] text-slate-400">({it.created_by})</span>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(it.date)}
                className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 shrink-0"
                data-testid={`adj-delete-${it.date}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
